<template>
  <div id="spectrumsList">
    <grid id="spectrumListGrid"
          :title="$t('g-spectrums:spectrums')"
          :columns="columns"
          :paginated-dto="spectrumPaginatedDto"
          @load="filter"
    >
      <template slot="date" slot-scope="props">{{ props.dto.date | date }}</template>
      <template slot="duration" slot-scope="props">{{ props.dto.duration * 1000 | duration('full') }}</template>

      <span slot="status" slot-scope="props" :class="{'text-danger': props.dto.status === 'KO', 'text-success': props.dto.status === 'OK', 'text-info': props.dto.status === 'PENDING'}">
        {{ props.dto.status }}
      </span>

      <div slot="actions" slot-scope="props" class="text-right">
        <router-link v-if="props.dto.status === 'OK'" :to="{name: 'gatewaySpectrumsViewer', params: {id: ''+props.dto.id}}"><span class="icon-play-circle" /></router-link>
        <span v-if="props.dto.status === 'OK'" class="icon-download" @click.prevent="download(props.dto)" />
        <i class="icon-trash" @click="confirmDelete(props.dto)" />
      </div>
    </grid>

    <confirm id="confirmDelete"
             v-model="showDeleteConfirm"
             :message="$t('g-spectrums:deleteSpectrum')"
             @confirm:validate="deleteSpectrum"
    />

    <floating-button :click-handler="() => { showSpectrumForm=true }" icon-classes="icon-add" />
    <spectrums-form v-model="showSpectrumForm" @success="filter" />
  </div>
</template>

<script>
import { isEmpty, find } from 'lodash-es'
import { mapGetters } from 'vuex'
import saveAs from 'file-saver'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

import SpectrumsForm from './SpectrumsForm'

export default {
  titleKey: 'gatewaySpectrums',

  components: { SpectrumsForm },

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      duration: '15',

      columns: [
        { text: this.$t('g-spectrums:date'), slot: 'date' },
        { text: this.$t('g-spectrums:modem'), formatter: dto => dto.modem },
        { text: this.$t('g-spectrums:duration'), slot: 'duration' },
        { text: this.$t('g-spectrums:status'), slot: 'status' },
        { slot: 'actions' }
      ],
      spectrumPaginatedDto: {},

      showDeleteConfirm: false,
      spectrumToDelete: null,
      // Create spectrum
      showSpectrumForm: false
    }
  },

  computed: {
    ...mapGetters(['gateway'])
  },

  watch: {
    eui () {
      // Reset data, and get data from new gateway
      this.spectrumPaginatedDto = {}
    },

    gateway () {
      // Do not refresh is we olready have the data
      if (!isEmpty(this.spectrumPaginatedDto)) return

      this.filter()
    }
  },

  created () {
    if (!isEmpty(this.gateway)) this.filter()
  },

  destroyed () {
    clearTimeout(this.timeoutGetSpectrums)
  },

  methods: {
    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      let search = []

      // Add gateway EUI
      search = this.$utils.addSearch(search, 'eq', this.eui, 'gateway.eui')

      // Add criteria

      // Format criteria
      if (search.length > 0) {
        options.search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))
      }

      // Get filtered data
      this.getSpectrums(options)
    },

    async getSpectrums (options = {}) {
      const poll = () => {
        if (find(this.spectrumPaginatedDto.list, { status: 'PENDING' })) {
          clearTimeout(this.timeoutGetSpectrums)
          // In case the promise ended after the destruction of the component
          // Check the component status to only relaunch the polling when component is not destroyed
          if (this._isDestroyed || this._isBeingDestroyed) return

          this.timeoutGetSpectrums = setTimeout(this.filter, 5000)
        }
      }

      const params = this.$utils.getParams({ sort: '-date' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getSpectrums(this.$store.getters.application, params))

      if (error || !response) {
        poll()
        return
      }

      this.spectrumPaginatedDto = response.data
      poll()
    },

    async download (spectrum) {
      let error, response
      [error, response] = await to(this.$api.getSpectrumFile(spectrum, { format: 'DECIMAL' }))
      if (error || !response) return

      const properties = { type: 'csv/text' }
      const filename = `spectrum-${spectrum.gateway.eui}-${spectrum.modem}-${spectrum.duration}`
      let csvFile
      try {
        csvFile = new File([response.data], filename + '.csv', properties)
      } catch (e) {
        csvFile = new Blob([response.data], properties)
      }
      saveAs(csvFile, filename + '.csv')
    },

    confirmDelete (spectrum) {
      this.showDeleteConfirm = true
      this.spectrumToDelete = spectrum
    },

    async deleteSpectrum () {
      let error
      [error] = await to(this.$api.deleteSpectrum(this.spectrumToDelete))
      if (error) return

      // Success
      eventBus.$emit('message-success', this.$t('g-spectrums:removeSuccess'))
      // Refresh list
      this.filter()
    }
  }
}
</script>
